import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Webhook, XCircle } from "lucide-react";
import { toast } from "sonner";
import client from "../../lib/api";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `há ${Math.floor(diff)}s`;
  if (diff < 3600) return `há ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  return `há ${Math.floor(diff / 86400)}d`;
};

const REASON_HINTS = {
  bad_signature: "EUPAGO_WEBHOOK_SECRET não bate com o do portal EuPago. Todos os webhooks são rejeitados — Premium não activa.",
  missing_identifier: "Payload do EuPago não incluiu `identifier`. Provavelmente formato inesperado.",
  tx_not_found: "Identifier existe mas não bate com nenhuma transacção local. URL de callback pode estar a apontar para outro ambiente.",
  paid_granted: "Pagamento aceite — Premium activado e Moloni em background.",
};

/** Admin diagnostic: real-time visibility into EuPago webhooks — every
 *  attempt (accepted or rejected) with the reason. Critical to debug prod
 *  issues like "customer paid but Premium didn't activate" WITHOUT SSH
 *  access to backend logs. Signatures/bodies are redacted server-side. */
const WebhookDiagSection = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [outcome, setOutcome] = useState("");  // "", "accepted", "rejected"
  const [reasonFilter, setReasonFilter] = useState("");  // client-side chip filter
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = outcome ? `?limit=50&outcome=${outcome}` : "?limit=50";
      const r = await client.get(`/admin/webhooks/eupago/recent${q}`);
      setRows(r.data?.rows || []);
      setSummary(r.data?.summary || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar webhooks.");
    } finally {
      setLoading(false);
    }
  }, [outcome]);

  useEffect(() => { load(); }, [load]);

  // Client-side chip filter — server already narrowed by outcome, chips
  // narrow further by outcome_reason without an extra roundtrip.
  const visibleRows = reasonFilter
    ? rows.filter((e) => e.outcome_reason === reasonFilter)
    : rows;

  const health = (() => {
    if (!summary) return null;
    const { total_24h, accepted_24h, rejected_24h } = summary;
    if (total_24h === 0) return { tone: "warn", text: "Zero webhooks nas últimas 24h — pode significar que EuPago não está a conseguir chegar-nos." };
    if (rejected_24h > 0 && accepted_24h === 0) return { tone: "danger", text: `${rejected_24h} rejeitados, zero aceites. Fluxo de pagamentos está BLOQUEADO — investiga o motivo dominante abaixo.` };
    if (rejected_24h > accepted_24h) return { tone: "warn", text: `${rejected_24h} rejeitados vs ${accepted_24h} aceites — mais do que metade a falhar.` };
    return { tone: "ok", text: `${accepted_24h} aceites nas últimas 24h.` };
  })();

  return (
    <section className="bg-[#161920] border border-white/5 rounded-2xl p-6"
             data-testid="webhook-diag-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#A29F9B] mb-1">
            <Webhook className="w-3.5 h-3.5 text-[#C5A880]" />
            Diagnóstico Webhooks EuPago
          </div>
          <h3 className="font-display text-2xl text-[#F5F2EE]">
            Últimas 24h
          </h3>
          <p className="text-xs text-[#A29F9B] mt-1 max-w-2xl">
            Se um cliente pagou mas o Premium não activou, procura aqui o motivo.
            Bodies e assinaturas ficam redigidos (só prefixo + hash SHA-256).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)}
                  className="bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-[#F5F2EE]"
                  data-testid="webhook-diag-filter">
            <option value="">Todos</option>
            <option value="accepted">Só aceites</option>
            <option value="rejected">Só rejeitados</option>
          </select>
          <button onClick={load} disabled={loading}
                  className="inline-flex items-center gap-2 text-sm text-[#A29F9B] hover:text-white disabled:opacity-50"
                  data-testid="webhook-diag-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Actualizar
          </button>
        </div>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="webhook-diag-summary">
          <div className="bg-[#0C0E12] border border-white/5 rounded-lg p-3">
            <div className="text-xs text-[#A29F9B] uppercase tracking-widest">Total 24h</div>
            <div className="font-display text-2xl text-[#F5F2EE] mt-1">{summary.total_24h}</div>
          </div>
          <div className="bg-[#0C0E12] border border-emerald-900/40 rounded-lg p-3">
            <div className="text-xs text-emerald-400 uppercase tracking-widest">Aceites</div>
            <div className="font-display text-2xl text-emerald-300 mt-1">{summary.accepted_24h}</div>
          </div>
          <div className="bg-[#0C0E12] border border-red-900/40 rounded-lg p-3">
            <div className="text-xs text-red-400 uppercase tracking-widest">Rejeitados</div>
            <div className="font-display text-2xl text-red-300 mt-1">{summary.rejected_24h}</div>
          </div>
        </div>
      )}

      {/* Health banner */}
      {health && (
        <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 mb-4 text-sm ${
              health.tone === "danger" ? "bg-red-900/20 border border-red-900/40 text-red-200" :
              health.tone === "warn" ? "bg-amber-900/20 border border-amber-900/40 text-amber-200" :
              "bg-emerald-900/20 border border-emerald-900/40 text-emerald-200"
            }`}
             data-testid={`webhook-diag-health-${health.tone}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{health.text}</span>
        </div>
      )}

      {/* Reasons breakdown — click a chip to filter rows below client-side */}
      {summary?.reasons_24h && Object.keys(summary.reasons_24h).length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="webhook-diag-reasons">
          {Object.entries(summary.reasons_24h)
            .sort((a, b) => b[1] - a[1])
            .map(([reason, count]) => {
              const active = reasonFilter === reason;
              return (
                <button key={reason} type="button"
                        onClick={() => setReasonFilter(active ? "" : reason)}
                        title={REASON_HINTS[reason] || "Clica para filtrar por este motivo"}
                        className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${
                          active
                            ? "bg-[#C5A880] text-[#0C0E12] border-[#C5A880] font-semibold"
                            : "bg-[#0C0E12] border-white/10 text-[#DCD7D2] hover:border-[#C5A880]/50"
                        }`}
                        data-testid={`webhook-diag-reason-${reason}`}>
                  <b>{count}×</b> {reason}
                </button>
              );
            })}
          {reasonFilter && (
            <button type="button" onClick={() => setReasonFilter("")}
                    className="text-xs text-[#A29F9B] hover:text-white underline ml-1"
                    data-testid="webhook-diag-reason-clear">
              limpar filtro
            </button>
          )}
        </div>
      )}

      {/* Rows */}
      {visibleRows.length === 0 && !loading && (
        <p className="text-sm text-[#A29F9B] italic">
          Nenhum webhook EuPago no filtro seleccionado.
        </p>
      )}

      {visibleRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#A29F9B] border-b border-white/5">
                <th className="py-2 pr-4">Quando</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2 pr-4">Motivo</th>
                <th className="py-2 pr-4">Identifier</th>
                <th className="py-2 pr-4">Assinatura</th>
                <th className="py-2 pr-4">Origem</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((e) => {
                const isRejected = e.outcome === "rejected";
                const rowExpanded = expanded === e.id;
                return (
                  <React.Fragment key={e.id}>
                    <tr onClick={() => setExpanded(rowExpanded ? null : e.id)}
                        className="border-b border-white/5 text-[#DCD7D2] cursor-pointer hover:bg-white/[.02]"
                        data-testid={`webhook-row-${e.id}`}>
                      <td className="py-2 pr-4 whitespace-nowrap text-xs">{fmtRelative(e.received_at)}</td>
                      <td className="py-2 pr-4">
                        {isRejected ? (
                          <span className="inline-flex items-center gap-1 text-xs text-red-300"><XCircle className="w-3.5 h-3.5" /> {e.outcome}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-300"><CheckCircle2 className="w-3.5 h-3.5" /> {e.outcome}</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <span className="text-xs">{e.outcome_reason}</span>
                        {REASON_HINTS[e.outcome_reason] && (
                          <div className="text-[11px] text-[#A29F9B] mt-0.5">{REASON_HINTS[e.outcome_reason]}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{e.identifier || "—"}</td>
                      <td className="py-2 pr-4 text-xs">
                        {e.signature_present ? (
                          <span className={e.signature_valid ? "text-emerald-300" : "text-red-300"}>
                            {e.signature_valid ? "válida" : "inválida"} ({e.signature_prefix || "?"}…)
                          </span>
                        ) : (
                          <span className="text-[#A29F9B]">ausente</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{e.source_ip || "—"}</td>
                    </tr>
                    {rowExpanded && (
                      <tr className="bg-[#0C0E12] border-b border-white/5"
                          data-testid={`webhook-row-detail-${e.id}`}>
                        <td colSpan="6" className="p-3">
                          <div className="grid md:grid-cols-2 gap-3 text-xs">
                            <div>
                              <div className="text-[#A29F9B] uppercase tracking-widest mb-1">Recebido</div>
                              <div className="font-mono">{e.received_at}</div>
                            </div>
                            {e.matched_session_id && (
                              <div>
                                <div className="text-[#A29F9B] uppercase tracking-widest mb-1">Session ID</div>
                                <div className="font-mono break-all">{e.matched_session_id}</div>
                              </div>
                            )}
                            {e.status_reported && (
                              <div>
                                <div className="text-[#A29F9B] uppercase tracking-widest mb-1">Status reportado por EuPago</div>
                                <div className="font-mono">{e.status_reported}</div>
                              </div>
                            )}
                            {e.body_hash && (
                              <div>
                                <div className="text-[#A29F9B] uppercase tracking-widest mb-1">SHA-256 do body</div>
                                <div className="font-mono break-all">{e.body_hash}</div>
                              </div>
                            )}
                          </div>
                          {e.body_preview && (
                            <div className="mt-3">
                              <div className="text-[#A29F9B] uppercase tracking-widest mb-1 text-xs">Preview do body (primeiros 200 chars, redigido)</div>
                              <pre className="bg-black/40 border border-white/5 rounded p-2 text-[11px] font-mono overflow-x-auto text-[#DCD7D2]">{e.body_preview}</pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default WebhookDiagSection;
